import { Redirect, Route, BrowserRouter as Router, Switch } from "react-router-dom";
import Page from "./components/Page";
import AffiliateCenter from "./screens/AffiliateCenter";
import Comments from "./screens/Comments";
import CustomerList from "./screens/CustomerList";
import Customers from "./screens/Customers";
import Drafts from "./screens/Drafts";
import Earning from "./screens/Earning";
import ExploreCreators from "./screens/ExploreCreators";
import MessageCenter from "./screens/MessageCenter";
import Conversations from "./screens/Conversations";
import Payouts from "./screens/Payouts";
import ProductsDashboard from "./screens/ProductsDashboard";
import Promote from "./screens/Promote";
import Refunds from "./screens/Refunds";
import Released from "./screens/Released";
import Scheduled from "./screens/Scheduled";
import Settings from "./screens/Settings";
import Shop from "./screens/Shop";
import SignIn from "./screens/SignIn";
import SignUp from "./screens/SignUp";
import Statements from "./screens/Statements";
import UpgradeToPro from "./screens/UpgradeToPro";
import "./styles/app.sass";

import { useEffect } from "react";
import { BiChevronRight } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { fetchBug } from "./app/bugs/bugs.slice";
import { fetchCards } from "./app/cards/cards.slice";
import { fetchAllDecks, fetchAllTexts, fetchInitialCatalog } from "./app/catalog/catalog.slice";
import { fetchCategory } from "./app/category/category.slice";
import { fetchClasses } from "./app/classes/classes.slice";
import { fetchClassification } from "./app/classification/classification.slice";
import { fetchGrammar } from "./app/grammar/grammar.slice";
import { fetchLoginUser } from "./app/user/user.slice";
import { fetchUsers } from "./app/users/users.slice";
import AddBug from "./screens/AddBug";
import BugsReports from "./screens/BugsReports";
import Cards from "./screens/Cards";
import Catalog from "./screens/Catalog";
import Classes from "./screens/Classes";

import NewCard from "./screens/NewCard";
import NewClasse from "./screens/NewClass";
import NewDeck from "./screens/NewDeck";
import Campaigns from "./screens/Campaigns";
import CampaignEditor from "./screens/Campaigns/Editor";
import Redirecting from "./screens/Redirecting/Redirecting";
import Home from "./screens/Home";
import AiDraftsScreen from "./screens/AiDrafts";
import AiDraftsReview from "./screens/AiDrafts/Review";
import Texts from "./screens/Texts";
import AddText from "./screens/Texts/Add";
import UsersComponent from "./screens/Users";
import Subscriptions from "./screens/Subscriptions";
import Referrals from "./screens/Referrals";
// import Content from "./screens/Texts/Content";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetchFeedback } from "./app/feedback/feedback.slice";
import i18n, { normalizeUserLang } from "./i18n";
import DecksImages from "./screens/DecksImage";
import SocialHub from "./screens/SocialHub";
import TextEditor from "./screens/Texts/TextEditor";
import Topics from "./screens/Topics";
import NewTopic from "./screens/Topics/Add";
import VideoTutorials from "./screens/VideoTutorials";


const REDIRECT_FROM_KEY = "auth_redirect_from";

/**
 * RedirectToLoginWithFrom — when unauthenticated user hits a protected route,
 * redirect to login but preserve the intended URL in state and sessionStorage
 * (sessionStorage survives OAuth redirects) so we can redirect back after login.
 */
function RedirectToLoginWithFrom() {
  const location = useLocation();
  const from = location.pathname + (location.search || "");
  try {
    sessionStorage.setItem(REDIRECT_FROM_KEY, from);
  } catch (_) {}
  return <Redirect to={{ pathname: "/", state: { from } }} />;
}

/**
 * HomeOrRedirect — when user lands on / after login, redirect to the URL they
 * intended (e.g. /texts/content/:id); otherwise render the Home dashboard.
 */
function HomeOrRedirect() {
  const { t } = useTranslation();
  const location = useLocation();
  let from = location.state?.from;
  if (!from) {
    try {
      from = sessionStorage.getItem(REDIRECT_FROM_KEY);
      if (from) sessionStorage.removeItem(REDIRECT_FROM_KEY);
    } catch (_) {}
  }
  if (from && from !== "/") {
    return <Redirect to={from} />;
  }
  return (
    <Page title={t("pages.home")}>
      <Home />
    </Page>
  );
}

/**
 * CatalogKeepAlive — renders <Catalog /> permanently in the DOM so its state
 * (loaded categories, scroll position, expanded nodes) is never lost when the
 * user navigates away.  When the path is not /catalog, the wrapper uses
 * display:none to remove it from layout/paint without unmounting React.
 */
function CatalogKeepAlive() {
  const { t } = useTranslation();
  const location = useLocation();
  const isActive = location.pathname === '/catalog';
  return (
    <div
      style={isActive ? undefined : { display: 'none' }}
      aria-hidden={!isActive}
    >
      <Page title={t('pages.catalog')} noVerticalPadding>
        <Catalog isActive={isActive} />
      </Page>
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);

  // Only treat as "not logged in" when we've finished checking. While loading, stay on current URL.
  const authChecked = !user.isLoadingLogin;
  const isLoggedIn = !!user.value;

  useEffect(() => {
    dispatch(fetchLoginUser());
  }, [dispatch]);

  // Set backoffice UI language from user preference (en, es, pt, ru, fr)
  useEffect(() => {
    if (user.value?.lang) {
      const locale = normalizeUserLang(user.value.lang);
      if (locale && i18n.language !== locale) {
        i18n.changeLanguage(locale);
      }
    }
  }, [user.value?.lang]);

  useEffect(() => {
    if (user.value) {
      if (user.value?.role === "USER") {
        localStorage.clear();
        toast.error(t("toast.noPermissions"));
        window.open("/", "_self");
        return;
      }
      // catalog/fetchAllDecks already fetches 5000 decks — no need for decks/fetchDecks
      dispatch(fetchAllDecks());       // → GET /catalog?type=deck&take=5000
      dispatch(fetchAllTexts());       // → GET /catalog?type=text&take=5000
      dispatch(fetchCards({ take: 50, skip: 0 })); // precarga inicial de cards
      dispatch(fetchInitialCatalog()); // precarga de la vista principal del catálogo
      dispatch(fetchClasses());
      dispatch(fetchCategory());
      dispatch(fetchGrammar());
      dispatch(fetchClassification());
      dispatch(fetchBug());
      dispatch(fetchUsers());
      dispatch(fetchFeedback());
    }
  }, [user.value, dispatch, t]);

  // While auth is loading, show loading and do NOT redirect — preserves URL on refresh
  if (!authChecked) {
    return (
      <Router>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div>{t('common.loading') || 'Loading...'}</div>
        </div>
      </Router>
    );
  }

  return (
    <Router>
      {user.value === null ? (
        <Switch>
            <Route exact path="/" render={() => <SignIn />} />
            <Route exact path="/sign-up" render={() => <SignUp />} />
            <Route exact path="/redirect_google" render={() => <Redirecting />} />
            <Route exact path="/bug/add" render={() => <AddBug isPadding />} />
            {/* Catch-all: redirect to login but preserve intended URL so we can redirect back after login */}
            <Route render={() => <RedirectToLoginWithFrom />} />
        </Switch>
        ) : (
        <Switch>
            <Route exact path="/" render={() => <HomeOrRedirect />} />

            <Route
              exact
              path="/ai-content/drafts"
              render={() => (
                <Page title={t("pages.aiDrafts")}>
                  <AiDraftsScreen />
                </Page>
              )}
            />

            <Route
              exact
              path="/ai-content/review"
              render={() => (
                <Page title={t("aiDrafts.reviewTitle")}>
                  <AiDraftsReview />
                </Page>
              )}
            />

            <Route
              exact
              path="/deck/add"
              render={() => (
                <Page title={t("pages.newDeck")}>
                  <NewDeck isNew />
                </Page>
              )}
            />
            <Route
              exact
              path="/deck/edit/:deckId"
              render={() => (
                <Page title={t("pages.editDeck")}>
                  <NewDeck />
                </Page>
              )}
            />
            <Route
              exact
              path="/deck-images"
              render={() => (
                <Page title={t("pages.deckImages")}>
                  <DecksImages />
                  {/* <Drafts /> */}
                </Page>
              )}
            />
            <Route
              exact
              path="/cards"
              render={() => (
                <Page title={t("pages.cards")}>
                  <Cards />
                  {/* <Drafts /> */}
                </Page>
              )}
            />
            <Route
              exact
              path="/card/add"
              render={() => (
                <Page isPadding={false} title={t("pages.newCard")}>
                  <NewCard isNew />
                  {/* <Drafts /> */}
                </Page>
              )}
            />

            <Route
              exact
              path="/texts"
              render={() => (
                <Page title={t("pages.texts")}>
                  <Texts />
                  {/* <Drafts /> */}
                </Page>
              )}
            />

            <Route
              exact
              path="/texts/add"
              render={() => (
                <Page isPadding={false} title={t("pages.addNewText")}>
                  <AddText />
                  {/* <Drafts /> */}
                </Page>
              )}
            />

            <Route
              exact
              path="/texts/edit/:id"
              render={({ location, match }) => {
                const search =
                  location?.search || (typeof window !== "undefined" ? window.location.search : "");
                const view = new URLSearchParams(search).get("view");
                if (view === "content") {
                  return <Redirect to={`/texts/content/${match.params.id}`} />;
                }
                return (
                  <Page isPadding={false} title={t("pages.editText")}>
                    <TextEditor key={`${match.params.id}-text`} initialMode="TEXT" />
                  </Page>
                );
              }}
            />

            <Route
              exact
              path="/texts/content/:id"
              render={() => (
                <Page isPadding={false} title="">
                  <TextEditor initialMode="CONTENT" />
                </Page>
              )}
            />

            <Route
                exact
                path="/topics"
                render={() => (
                    <Page title={t("pages.topics")}>
                      <Topics />
                      {/* <Drafts /> */}
                    </Page>
                )}
            />
            {
              <Route
                exact
                path="/topic/add"
                render={() => (
                    <Page title={t("pages.topics")}>
                      <NewTopic isNew={true} />
                    </Page>
                )}
            />
            }
            {


                <Route
                  exact
                  path="/topics/edit/:topicId"
                  render={() => (
                      <Page title={t("pages.topics")}>
                        <NewTopic isNew={false} />
                      </Page>
                  )}
              />
            }

            <Route
              exact
              path="/card/edit/:cardId"
              render={() => (
                <Page isPadding={false} title={t("pages.editCard")}>
                  <NewCard />
                  {/* <Drafts /> */}
                </Page>
              )}
            />

            {/* El antiguo sistema de Notifications (news) fue sustituido por Campañas.
                La ruta vieja redirige para no romper marcadores. */}
            <Route exact path="/notifications" render={() => <Redirect to="/campaigns" />} />

            <Route
              exact
              path="/campaigns"
              render={() => (
                <Page title={t("pages.notifications")}>
                  <Campaigns />
                </Page>
              )}
            />
            <Route
              exact
              path="/campaigns/new"
              render={() => (
                <Page title={t("pages.notifications")}>
                  <CampaignEditor />
                </Page>
              )}
            />
            <Route
              exact
              path="/campaigns/:id"
              render={() => (
                <Page title={t("pages.notifications")}>
                  <CampaignEditor />
                </Page>
              )}
            />

            <Route
              exact
              path="/social-hub"
              render={() => (
                <Page title={t("pages.socialHub")}>
                  <SocialHub />
                </Page>
              )}
            />
            <Route
              exact
              path="/videotutorials"
              render={() => (
                <Page title={t("pages.videotutorials")}>
                  <VideoTutorials />
                </Page>
              )}
            />







            <Route
              exact
              path="/users"
              render={() => (
                <Page title={t("pages.users")}>
                  <UsersComponent />
                  {/* <Drafts /> */}
                </Page>
              )}
            />

            <Route
              exact
              path="/subscriptions"
              render={() => (
                <Page title="Suscripciones">
                  <Subscriptions />
                </Page>
              )}
            />

            <Route
              exact
              path="/referrals"
              render={() => (
                <Page title={t("referrals.title")}>
                  <Referrals />
                </Page>
              )}
            />

            <Route
              exact
              path="/classes"
              render={() => (
                <Page title={t("pages.classes")}>
                  <Classes />
                  {/* <Drafts /> */}
                </Page>
              )}
            />
            <Route
              exact
              path="/classes/add"
              render={() => (
                <Page title={t("pages.newClass")}>
                  <NewClasse />
                </Page>
              )}
            />

            {/* /catalog is handled by CatalogKeepAlive below (outside Switch) so it is never unmounted */}
            <Route exact path="/categories" render={() => <Redirect to="/catalog" />} />

            <Route
              exact
              path="/classes/edit/:classeId"
              render={() => (
                <Page title={t("pages.editClass")}>
                  <NewClasse />
                </Page>
              )}
            />

            <Route
              exact
              path="/bug-reports"
              render={() => (
                <Page title={t("pages.bugsReports")}>
                  <BugsReports />
                  {/* <Drafts /> */}
                </Page>
              )}
            />

            {/* El feedback ya no tiene pantalla propia: todo vive en
                Conversaciones, como hilo. La ruta se mantiene por los enlaces
                antiguos y redirige. */}
            <Route exact path="/feedback" render={() => <Redirect to="/conversations" />} />

            <Route
              exact
              path="/products/dashboard"
              render={() => (
                <Page title={t("pages.productsDashboard")}>
                  <ProductsDashboard />
                </Page>
              )}
            />

            <Route
              exact
              path="/products/drafts"
              render={() => (
                <Page title={t("pages.drafts")}>
                  <Drafts />
                </Page>
              )}
            />
            <Route
              exact
              path="/products/released"
              render={() => (
                <Page title={t("pages.released")}>
                  <Released />
                </Page>
              )}
            />
            <Route
              exact
              path="/products/comments"
              render={() => (
                <Page title={t("pages.comments")}>
                  <Comments />
                </Page>
              )}
            />
            <Route
              exact
              path="/products/scheduled"
              render={() => (
                <Page title={t("pages.scheduled")}>
                  <Scheduled />
                </Page>
              )}
            />
            <Route
              exact
              path="/customers/overview"
              render={() => (
                <Page title={t("pages.customers")}>
                  <Customers />
                </Page>
              )}
            />
            <Route
              exact
              path="/customers/customer-list"
              render={() => (
                <Page title={t("pages.customerList")}>
                  <CustomerList />
                </Page>
              )}
            />
            <Route
              exact
              path="/shop"
              render={() => (
                <Page wide>
                  <Shop />
                </Page>
              )}
            />
            <Route
              exact
              path="/income/earning"
              render={() => (
                <Page title={t("pages.earning")}>
                  <Earning />
                </Page>
              )}
            />
            <Route
              exact
              path="/income/refunds"
              render={() => (
                <Page title={t("pages.refunds")}>
                  <Refunds />
                </Page>
              )}
            />
            <Route
              exact
              path="/income/payouts"
              render={() => (
                <Page title={t("pages.payouts")}>
                  <Payouts />
                </Page>
              )}
            />
            <Route
              exact
              path="/income/statements"
              render={() => (
                <Page title={t("pages.statements")}>
                  <Statements />
                </Page>
              )}
            />
            <Route
              exact
              path="/promote"
              render={() => (
                <Page title={t("pages.promote")}>
                  <Promote />
                </Page>
              )}
            />
            <Route
              exact
              path="/conversations"
              render={() => (
                <Page title="Conversaciones">
                  <Conversations />
                </Page>
              )}
            />
            <Route
              exact
              path="/conversations/:id"
              render={() => (
                <Page title="Conversaciones">
                  <Conversations />
                </Page>
              )}
            />
            <Route
              exact
              path="/settings"
              render={() => (
                <Page title={t("pages.settings")}>
                  <Settings />
                </Page>
              )}
            />
            <Route
              exact
              path="/upgrade-to-pro"
              render={() => (
                <Page title={t("pages.upgradeToPro")}>
                  <UpgradeToPro />
                </Page>
              )}
            />
            <Route
              exact
              path="/message-center"
              render={() => (
                <Page title={t("pages.messageCenter")}>
                  <MessageCenter />
                </Page>
              )}
            />
            <Route
              exact
              path="/explore-creators"
              render={() => (
                <Page title={t("pages.exploreCreators")}>
                  <ExploreCreators />
                </Page>
              )}
            />
            <Route
              exact
              path="/affiliate-center"
              render={() => (
                <Page title={t("pages.affiliateCenter")}>
                  <AffiliateCenter />
                </Page>
              )}
            />
        </Switch>
        )}

        {/* <Route exact path="/" render={() => <SignIn />} /> */}
        {/* <Route exact path="/pagelist" component={PageList} /> */}
      {/* Keep-alive: Catalog stays mounted for the lifetime of the session so its state (scroll, loaded data, expanded rows) is preserved across navigation. */}
      {user.value && user.value.role !== 'USER' && <CatalogKeepAlive isLoggedIn />}
    </Router>
  );
}

export default App;
